import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import DirectionsBoatOutlined from "@mui/icons-material/DirectionsBoatOutlined";
import FactoryOutlined from "@mui/icons-material/FactoryOutlined";
import OilBarrelOutlined from "@mui/icons-material/OilBarrelOutlined";
import WavesOutlined from "@mui/icons-material/WavesOutlined";
import ScienceOutlined from "@mui/icons-material/ScienceOutlined";
import BoltOutlined from "@mui/icons-material/BoltOutlined";
import {
  ONTOLOGY_BUSINESS_AREA_OPTIONS,
  DEFAULT_SELECTED_BUSINESS_AREA
} from "../../data/create-ontology-initiative.data";
import { ONTOLOGY_WORKBENCH_STEPS } from "../../data/create-ontology-questions.data";
import {
  OntologyService,
  CreateOntologyInitiativeRequest
} from "../../data/ontology.service";
import "./CreateOntologyInitiative.scss";

const BUSINESS_AREA_ICONS: Record<string, React.ReactNode> = {
  "upstream-offshore": <DirectionsBoatOutlined fontSize="small" />,
  "upstream-unconventionals": <FactoryOutlined fontSize="small" />,
  midstream: <OilBarrelOutlined fontSize="small" />,
  refining: <WavesOutlined fontSize="small" />,
  chemicals: <ScienceOutlined fontSize="small" />,
  "low-carbon": <BoltOutlined fontSize="small" />
};

const CreateOntologyInitiative: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const editingInitiativeId = (
    location.state as { initiativeId?: string } | null
  )?.initiativeId;
  const [selectedBusinessAreas, setSelectedBusinessAreas] = useState<string[]>([
    DEFAULT_SELECTED_BUSINESS_AREA
  ]);
  const [form, setForm] = useState({
    name: "",
    businessObjective: "",
    businessValue: "",
    scope: "",
    exclusions: ""
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingInitiative, setIsLoadingInitiative] = useState(
    !!editingInitiativeId
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Edit mode: there's no GET-by-id endpoint, so prefill from the list response.
  // Note: scope/exclusions aren't part of that response, so they can't be prefilled.
  useEffect(() => {
    if (!editingInitiativeId) return;
    let isMounted = true;
    OntologyService.getInitiativeById(editingInitiativeId).then(
      (initiative) => {
        if (!isMounted || !initiative) {
          if (isMounted) setIsLoadingInitiative(false);
          return;
        }
        setForm((prev) => ({
          ...prev,
          name: initiative.initiativeName,
          businessObjective: initiative.businessObjective ?? "",
          businessValue: initiative.businessValue ?? "",
          scope: initiative.scope ?? "",
          exclusions: initiative.exclusions ?? ""
        }));
        const matchingIds = ONTOLOGY_BUSINESS_AREA_OPTIONS.filter((option) =>
          initiative.businessAreaName.includes(option.label)
        ).map((option) => option.id);
        if (matchingIds.length > 0) setSelectedBusinessAreas(matchingIds);
        setIsLoadingInitiative(false);
      }
    );
    return () => {
      isMounted = false;
    };
  }, [editingInitiativeId]);

  const toggleBusinessArea = (id: string) => {
    setSelectedBusinessAreas((prev) =>
      prev.includes(id) ? prev.filter((areaId) => areaId !== id) : [...prev, id]
    );
  };

  const updateForm =
    (field: keyof typeof form) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setForm((prev) => ({ ...prev, [field]: event.target.value }));
    };

  const validate = (): boolean => {
    if (!form.name.trim() || !form.businessObjective.trim()) {
      setErrorMessage("Initiative Name and Business Objective are required.");
      return false;
    }
    setErrorMessage(null);
    return true;
  };

  const buildPayload = (): CreateOntologyInitiativeRequest => {
    const businessAreaNames = ONTOLOGY_BUSINESS_AREA_OPTIONS.filter((option) =>
      selectedBusinessAreas.includes(option.id)
    ).map((option) => option.label);

    return {
      initiativeName: form.name.trim(),
      businessAreaNames,
      businessObjective: form.businessObjective.trim(),
      businessValue: form.businessValue.trim() || null,
      scope: form.scope.trim() || null,
      exclusions: form.exclusions.trim() || null,
      retriggerFromCompetencyQuestion: false
    };
  };

  const handleSaveDraft = async () => {
    if (!validate()) return;
    setIsSaving(true);
    try {
      if (editingInitiativeId) {
        await OntologyService.updateOntologyInitiative(
          editingInitiativeId,
          buildPayload()
        );
      } else {
        await OntologyService.createOntologyInitiative(buildPayload());
      }
      // navigate("/create-ontology");
    } catch (error) {
      setErrorMessage("Failed to save draft. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleContinue = async () => {
    if (!validate()) return;
    setIsSaving(true);
    try {
      const initiativeId = editingInitiativeId
        ? (
            await OntologyService.updateOntologyInitiative(
              editingInitiativeId,
              buildPayload()
            )
          ).initiativeId
        : (await OntologyService.createOntologyInitiative(buildPayload()))
            .initiativeId;
      navigate("/ontology-studio/create/questions", {
        state: { initiativeId }
      });
    } catch (error) {
      setErrorMessage("Failed to save initiative. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="create-initiative-container">
      <nav className="create-initiative-breadcrumb">
        <Link to="/home" className="breadcrumb-link">
          Home
        </Link>
        <span className="breadcrumb-separator">&gt;</span>
        <Link to="/create-ontology" className="breadcrumb-link">
          Ontology Studio
        </Link>
        <span className="breadcrumb-separator">&gt;</span>
        <span className="breadcrumb-current">Create Initiative</span>
      </nav>

      <div className="create-initiative-header">
        <h1 className="create-initiative-title">Create Ontology Initiative</h1>
        <p className="create-initiative-description">
          Create an ontology initiative by defining its business purpose, domain
          scope, stakeholders and supporting information.
        </p>
        <div className="create-initiative-meta">
          <span className="create-initiative-meta__pill">
            Initiative ID{" "}
            <strong>{editingInitiativeId ?? "EY-ONT-2026-TEMP"}</strong>
          </span>
          <span className="create-initiative-meta__pill">
            Status <strong>Draft</strong>
          </span>
        </div>
      </div>

      <div className="create-initiative-workflow">
        {ONTOLOGY_WORKBENCH_STEPS.map((step) => (
          <div
            key={step.id}
            className={`create-initiative-workflow__step${
              step.id === 1 ? " create-initiative-workflow__step--active" : ""
            }`}
          >
            <span className="create-initiative-workflow__indicator">
              {step.id === 1 ? "•" : ""}
            </span>
            <span className="create-initiative-workflow__label">
              {step.label}
            </span>
          </div>
        ))}
      </div>

      <section className="create-initiative-card">
        <div className="create-initiative-card__header">
          <h3 className="create-initiative-card__title">
            1. Business Area{" "}
            <span className="create-initiative-badge">Multiselect</span>
          </h3>
        </div>
        <div className="create-initiative-business-area-grid">
          {ONTOLOGY_BUSINESS_AREA_OPTIONS.map((option) => (
            <button
              key={option.id}
              type="button"
              className={`create-initiative-business-area-card${
                selectedBusinessAreas.includes(option.id)
                  ? " create-initiative-business-area-card--selected"
                  : ""
              }`}
              onClick={() => toggleBusinessArea(option.id)}
            >
              <span className="create-initiative-business-area-card__icon">
                {BUSINESS_AREA_ICONS[option.id]}
              </span>
              <span className="create-initiative-business-area-card__text">
                <span className="create-initiative-business-area-card__label">
                  {option.label}
                </span>
                <span className="create-initiative-business-area-card__description">
                  {option.description}
                </span>
              </span>
            </button>
          ))}
        </div>
      </section>

      <section className="create-initiative-card">
        <h3 className="create-initiative-card__title">
          2. Initiative Information
        </h3>
        <div className="create-initiative-form">
          <label className="create-initiative-field create-initiative-field--full">
            <span className="create-initiative-field__label">
              Initiative Name <span className="required-tag">*</span>
            </span>
            <input
              type="text"
              className="create-initiative-input"
              placeholder="FPSO Equipment Reliability Ontology"
              value={form.name}
              onChange={updateForm("name")}
            />
          </label>

          <label className="create-initiative-field">
            <span className="create-initiative-field__label">
              Business Objective <span className="required-tag">*</span>
            </span>
            <textarea
              className="create-initiative-textarea"
              placeholder="Describe the business problem this ontology will address."
              value={form.businessObjective}
              onChange={updateForm("businessObjective")}
            />
          </label>

          <label className="create-initiative-field">
            <span className="create-initiative-field__label">
              Business Value <span className="optional-tag">(Optional)</span>
            </span>
            <textarea
              className="create-initiative-textarea"
              placeholder="Explain expected outcomes and measurable benefits."
              value={form.businessValue}
              onChange={updateForm("businessValue")}
            />
          </label>

          <label className="create-initiative-field">
            <span className="create-initiative-field__label">
              Scope <span className="optional-tag">(Optional)</span>
            </span>
            <textarea
              className="create-initiative-textarea"
              placeholder="Define what is included."
              value={form.scope}
              onChange={updateForm("scope")}
            />
          </label>

          <label className="create-initiative-field">
            <span className="create-initiative-field__label">
              Exclusions <span className="optional-tag">(Optional)</span>
            </span>
            <textarea
              className="create-initiative-textarea"
              placeholder="Define what is intentionally excluded."
              value={form.exclusions}
              onChange={updateForm("exclusions")}
            />
          </label>
        </div>
      </section>

      <div className="create-initiative-footer">
        <div className="create-initiative-footer__right">
          {errorMessage && (
            <span className="create-initiative-error">{errorMessage}</span>
          )}
          <button
            type="button"
            className="create-initiative-btn create-initiative-btn--secondary"
            onClick={handleSaveDraft}
            disabled={isSaving || isLoadingInitiative}
          >
            {isSaving ? "Saving..." : "Save Draft"}
          </button>
          <button
            type="button"
            className="create-initiative-btn create-initiative-btn--primary"
            onClick={handleContinue}
            disabled={isSaving || isLoadingInitiative}
          >
            {isSaving ? "Please wait..." : "Continue"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateOntologyInitiative;
